import { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { BUSINESS_TYPES, REQUIREMENT_OPTIONS } from "../data/content";
import { leadMessage, waLink } from "../lib/whatsapp";
import { track } from "../lib/analytics";
import { recordRealActivity } from "../data/activityData";

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors focus:border-ember/60 focus:bg-white/[0.07]";

export default function LeadForm({
  extended = false,
  submitLabel = "Get a Free Website Plan",
  testIdPrefix = "lead-form",
  source = "hero",
  onSubmitted,
}) {
  const started = useRef(false);
  const onFirstFocus = () => {
    if (!started.current) {
      started.current = true;
      track("form_start", { source });
    }
  };
  const [form, setForm] = useState({
    name: "",
    business: "",
    phone: "",
    type: "",
    city: "",
    requirement: "",
    details: "",
  });
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.business.trim() || !form.phone.trim()) {
      setError("Please add your name, business name and WhatsApp number.");
      return;
    }
    if (!/^[\d\s+\-]{10,15}$/.test(form.phone.trim())) {
      setError("Please enter a valid WhatsApp number.");
      return;
    }
    setError("");
    track("lead_form_submit", { source, ...form });
    recordRealActivity(form);
    onSubmitted?.(form, waLink(leadMessage(form)));
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid={`${testIdPrefix}-form`}>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          aria-label="Your name"
          name="name"
          data-testid={`${testIdPrefix}-name-input`}
          value={form.name}
          onChange={set("name")}
          onFocus={onFirstFocus}
          placeholder="Your Name *"
          className={inputCls}
          autoComplete="name"
        />
        <input
          aria-label="Business name"
          name="business"
          data-testid={`${testIdPrefix}-business-input`}
          value={form.business}
          onChange={set("business")}
          placeholder="Business Name *"
          className={inputCls}
          autoComplete="organization"
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <input
          aria-label="WhatsApp number"
          name="phone"
          data-testid={`${testIdPrefix}-phone-input`}
          value={form.phone}
          onChange={set("phone")}
          placeholder="WhatsApp Number *"
          inputMode="tel"
          className={inputCls}
          autoComplete="tel"
        />
        <select
          aria-label="Business type"
          name="type"
          data-testid={`${testIdPrefix}-type-select`}
          value={form.type}
          onChange={set("type")}
          className={`${inputCls} appearance-none ${form.type ? "" : "text-white/30"}`}
        >
          <option value="" disabled className="bg-ink">Business Type</option>
          {BUSINESS_TYPES.map((t) => (
            <option key={t} value={t} className="bg-ink text-white">{t}</option>
          ))}
        </select>
      </div>
      {extended && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              aria-label="City"
          name="city"
          data-testid={`${testIdPrefix}-city-input`}
              value={form.city}
              onChange={set("city")}
              placeholder="City"
              className={inputCls}
              autoComplete="address-level2"
            />
            <select
              aria-label="What do you need?"
          name="requirement"
          data-testid={`${testIdPrefix}-requirement-select`}
              value={form.requirement}
              onChange={set("requirement")}
              className={`${inputCls} appearance-none ${form.requirement ? "" : "text-white/30"}`}
            >
              <option value="" disabled className="bg-ink">What do you need?</option>
              {REQUIREMENT_OPTIONS.map((r) => (
                <option key={r} value={r} className="bg-ink text-white">{r}</option>
              ))}
            </select>
          </div>
          <textarea
            aria-label="Tell us about your business"
          name="details"
          data-testid={`${testIdPrefix}-details-input`}
            value={form.details}
            onChange={set("details")}
            placeholder="Tell me about your business (optional)"
            rows={3}
            className={`${inputCls} resize-none`}
          />
        </>
      )}
      {error && (
        <p data-testid={`${testIdPrefix}-error`} className="text-xs text-ember">{error}</p>
      )}
      <button
        type="submit"
        data-testid={`${testIdPrefix}-submit-btn`}
        className="group flex w-full items-center justify-center gap-2 rounded-xl bg-ember py-3.5 font-display text-sm font-bold uppercase tracking-wider text-white transition-all hover:bg-ember-hover"
      >
        {submitLabel}
        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}
